<template>
    <div class="home">
        <!-- 轮播图部分 -->
        <section class="banner">
            <el-carousel height="600px" :interval="5000">
                <el-carousel-item v-for="item in bannerList" :key="item.id">
                    <div class="banner-item" :style="{ backgroundImage: `url(${item.image})` }">
                        <div class="banner-content">
                            <h2>{{ item.title }}</h2>
                            <p>{{ item.description }}</p>
                            <el-button type="primary" size="large" @click="navigateTo(item.link)">
                                {{ item.buttonText }}
                            </el-button>
                        </div>
                    </div>
                </el-carousel-item>
            </el-carousel>
        </section>

        <!-- 公司介绍部分 -->
        <section class="company-intro">
            <div class="container">
                <div class="intro-content">
                    <div class="intro-text">
                        <h1 class="section-title">郑州云轩网络科技有限公司企业数字化服务</h1>
                        <div class="divider"></div>
                        <p class="description">
                            郑州云轩网络科技有限公司专注于软件开发、AI 技术应用和数字化服务。
                            我们致力于为企业提供全方位的数字化解决方案，帮助企业实现数字化转型。
                        </p>
                        <div class="company-features">
                            <div class="feature-item" v-for="item in companyFeatures" :key="item.title">
                                <el-icon :size="40" :color="item.color">
                                    <component :is="item.icon" />
                                </el-icon>
                                <h3>{{ item.title }}</h3>
                                <p>{{ item.description }}</p>
                            </div>
                        </div>
                        <el-button type="primary" @click="navigateTo('/about')">了解更多</el-button>
                    </div>
                    <div class="company-image">
                        <el-image src="/images/home/company-intro.jpg" fit="cover" :lazy="true"
                            alt="郑州云轩网络科技有限公司办公与团队介绍" />
                    </div>
                </div>
            </div>
        </section>

        <!-- 业务领域部分 -->
        <section class="business-areas">
            <div class="container">
                <h2 class="section-title text-center">业务领域</h2>
                <div class="divider center"></div>
                <div class="business-grid">
                    <div class="business-card" v-for="item in businessAreas" :key="item.title"
                        @click="navigateTo('/business')">
                        <div class="card-icon">
                            <el-icon :size="50">
                                <component :is="item.icon" />
                            </el-icon>
                        </div>
                        <h3>{{ item.title }}</h3>
                        <p>{{ item.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- 成功案例 -->
        <section class="news-section">
            <div class="container">
                <h2 class="section-title text-center">成功案例</h2>
                <div class="divider center"></div>
                <div class="news-grid">
                    <div class="news-card" v-for="item in featuredCases" :key="item.id"
                        @click="navigateTo(`/success-cases/${item.id}`)">
                        <div class="news-image">
                            <img :src="item.image" :alt="item.title">
                            <div class="news-date">
                                <span class="day">{{ formatDay(item.date) }}</span>
                                <span class="month">{{ formatMonth(item.date) }}</span>
                            </div>
                        </div>
                        <div class="news-content">
                            <h3>{{ item.title }}</h3>
                            <p>{{ item.description }}</p>
                        </div>
                    </div>
                </div>
                <div class="text-center mt-4">
                    <el-button type="primary" @click="navigateTo('/success-cases')">查看更多</el-button>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
    Monitor,
    Connection,
    DataAnalysis,
    Promotion
} from '@element-plus/icons-vue'
import { successCases } from '@/data/successCases'

const router = useRouter()

const featuredCases = computed(() => successCases.slice(0, 3))

// 轮播图数据
const bannerList = [
    {
        id: 1,
        title: '数字化转型专家',
        description: '为企业提供全方位的数字化解决方案',
        image: '/images/home/banner-1.jpg',
        buttonText: '了解更多',
        link: '/about'
    },
    {
        id: 2,
        title: 'AI技术创新',
        description: '引领人工智能技术的商业化应用',
        image: '/images/home/banner-2.jpg',
        buttonText: '查看详情',
        link: '/business'
    },
    {
        id: 3,
        title: '专业技术团队',
        description: '拥有经验丰富的研发团队，提供优质服务',
        image: '/images/home/banner-3.jpg',
        buttonText: '联系我们',
        link: '/contact'
    }
]

// 公司特点
const companyFeatures = [
    {
        icon: 'Trophy',
        color: '#1890ff',
        title: '专业技术',
        description: '拥有资深的技术团队和丰富的项目经验'
    },
    {
        icon: 'User',
        color: '#52c41a',
        title: '优质服务',
        description: '提供全方位的技术支持和售后服务'
    },
    {
        icon: 'Opportunity',
        color: '#faad14',
        title: '创新能力',
        description: '持续创新，紧跟技术发展前沿'
    }
]

// 业务领域
const businessAreas = [
    {
        icon: Monitor,
        title: '软件开发',
        description: '提供专业的定制化软件开发服务，包括企业管理系统、移动应用等'
    },
    {
        icon: Connection,
        title: 'AI技术应用',
        description: '人工智能技术的商业化应用，包括机器学习、计算机视觉等'
    },
    {
        icon: DataAnalysis,
        title: '数据分析',
        description: '大数据分析和挖掘，为企业决策提供数据支持'
    },
    {
        icon: Promotion,
        title: '数字营销',
        description: '提供全方位的数字营销解决方案，助力企业品牌建设'
    }
]

// 页面跳转
const navigateTo = (path: string) => {
    router.push(path)
}

// 格式化日期
const formatDay = (date: string) => {
    return new Date(date).getDate()
}

const formatMonth = (date: string) => {
    return new Date(date).toLocaleString('zh-CN', { month: 'short' })
}
</script>

<style scoped lang="scss">
.home {
    padding-top: 80px; // 为固定导航栏留出空间

    .banner {
        .banner-item {
            height: 100%;
            background-size: cover;
            background-position: center;
            position: relative;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.5);
            }

            .banner-content {
                position: relative;
                z-index: 1;
                max-width: 1200px;
                margin: 0 auto;
                padding: 100px 20px;
                color: #fff;
                text-align: center;

                h2 {
                    font-size: 48px;
                    margin-bottom: 20px;
                    animation: slideUp 1s ease;
                }

                p {
                    font-size: 24px;
                    margin-bottom: 30px;
                    animation: slideUp 1s ease 0.2s;
                }

                .el-button {
                    animation: slideUp 1s ease 0.4s;
                }
            }
        }
    }

    .section-title {
        font-size: 36px;
        color: #333;
        margin-bottom: 20px;
    }

    .divider {
        width: 60px;
        height: 3px;
        background: var(--el-color-primary);
        margin-bottom: 40px;

        &.center {
            margin-left: auto;
            margin-right: auto;
        }
    }

    .company-intro {
        padding: 80px 0;
        background: #fff;

        .intro-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 60px;
            align-items: center;

            .intro-text {
                .description {
                    font-size: 16px;
                    line-height: 1.8;
                    color: #666;
                    margin-bottom: 40px;
                }

                .company-features {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 30px;
                    margin-bottom: 40px;

                    .feature-item {
                        text-align: center;

                        h3 {
                            font-size: 18px;
                            margin: 15px 0 10px;
                        }

                        p {
                            font-size: 14px;
                            color: #666;
                        }
                    }
                }
            }

            .company-image {
                img {
                    width: 100%;
                    border-radius: 12px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                }
            }
        }
    }

    .business-areas {
        padding: 80px 0;
        background: #f8f9fa;

        .business-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            margin-top: 40px;

            .business-card {
                background: #fff;
                padding: 40px 30px;
                border-radius: 12px;
                text-align: center;
                transition: transform 0.3s;
                cursor: pointer;

                &:hover {
                    transform: translateY(-10px);
                }

                .card-icon {
                    margin-bottom: 20px;
                    color: var(--el-color-primary);
                }

                h3 {
                    font-size: 20px;
                    margin-bottom: 15px;
                }

                p {
                    font-size: 14px;
                    color: #666;
                    line-height: 1.6;
                }
            }
        }
    }

    .news-section {
        padding: 80px 0;
        background: #fff;

        .news-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            margin-top: 40px;

            .news-card {
                background: #fff;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                cursor: pointer;
                transition: transform 0.3s;

                &:hover {
                    transform: translateY(-10px);
                }

                .news-image {
                    position: relative;
                    height: 200px;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .news-date {
                        position: absolute;
                        top: 20px;
                        left: 20px;
                        background: var(--el-color-primary);
                        color: #fff;
                        padding: 10px;
                        border-radius: 6px;
                        text-align: center;

                        .day {
                            display: block;
                            font-size: 24px;
                            font-weight: bold;
                            line-height: 1;
                        }

                        .month {
                            display: block;
                            font-size: 14px;
                            margin-top: 5px;
                        }
                    }
                }

                .news-content {
                    padding: 20px;

                    h3 {
                        font-size: 18px;
                        margin-bottom: 10px;
                        line-height: 1.4;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    p {
                        font-size: 14px;
                        color: #666;
                        line-height: 1.6;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }
                }
            }
        }
    }
}

@media (max-width: 1200px) {
    .home {
        .business-areas .business-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .news-section .news-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }
}

@media (max-width: 768px) {
    .home {
        .banner .banner-item .banner-content {
            h2 {
                font-size: 36px;
            }

            p {
                font-size: 18px;
            }
        }

        .company-intro {
            .intro-content {
                grid-template-columns: 1fr;
                gap: 40px;

                .company-features {
                    grid-template-columns: 1fr;
                }
            }
        }

        .business-areas .business-grid,
        .news-section .news-grid {
            grid-template-columns: 1fr;
        }
    }
}
</style>
